import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { AdminPageLayout, StatsGrid } from '@/components/layout/AdminPageLayout';
import { StatCard } from '@/components/layout/AdminStatCard';
import { Card } from '@/components/ui/card';
import { FileText, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import WarrantyCertificate from '@/components/WarrantyCertificate';

const WarrantyAgreementsPage = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showCertificate, setShowCertificate] = useState<any>(null);
  const [agreements, setAgreements] = useState<any[]>([]);
  const [equipment, setEquipment] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [people, setPeople] = useState<any[]>([]);
  const [contractors, setContractors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    pendingDelivery: 0,
    expiringSoon: 0
  });

  const [formData, setFormData] = useState({
    equipmentId: '',
    productId: '',
    homeownerId: '',
    contractorId: '',
    termMonths: 36,
    startDate: '',
    premiumCents: 0,
    deliveryMethod: 'email'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    if (!supabase) return;

    try {
      // Load agreements with related data
      const { data: agreementsData } = await supabase
        .from('warranty_agreements')
        .select(`
          *,
          product:warranty_products(*),
          equipment:equipment(*,
            manufacturer:manufacturers(*),
            property:properties(*)
          ),
          homeowner:people(*),
          contractor:contractors(*)
        `)
        .order('created_at', { ascending: false });

      setAgreements(agreementsData || []);

      // Load equipment available for agreements
      const { data: equipmentData } = await supabase
        .from('equipment')
        .select(`
          *,
          manufacturer:manufacturers(*),
          property:properties(*)
        `)
        .order('created_at', { ascending: false });

      setEquipment(equipmentData || []);

      // Load warranty products
      const { data: productsData } = await supabase
        .from('warranty_products')
        .select('*')
        .eq('active', true)
        .order('name');

      setProducts(productsData || []);

      // Load people (homeowners)
      const { data: peopleData } = await supabase
        .from('people')
        .select('*')
        .eq('role', 'homeowner')
        .order('last_name');

      setPeople(peopleData || []);

      // Load contractors
      const { data: contractorsData } = await supabase
        .from('contractors')
        .select('*')
        .eq('active', true)
        .order('legal_name');

      setContractors(contractorsData || []);

      // Calculate stats
      const totalAgreements = agreementsData?.length || 0;
      const activeAgreements = agreementsData?.filter(a => a.status === 'active').length || 0;
      const pendingDelivery = agreementsData?.filter(a => a.status === 'pending_delivery').length || 0;

      // Calculate expiring soon (within 30 days)
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
      const expiringSoon = agreementsData?.filter(a =>
        a.status === 'active' && new Date(a.end_date) <= thirtyDaysFromNow
      ).length || 0;

      setStats({
        total: totalAgreements,
        active: activeAgreements,
        pendingDelivery,
        expiringSoon
      });

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) return;

    try {
      // Calculate end date
      const startDate = new Date(formData.startDate);
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + formData.termMonths);

      // Calculate delivery deadline (45 days from creation per Florida law)
      const deliveryDeadline = new Date();
      deliveryDeadline.setDate(deliveryDeadline.getDate() + 45);

      // Find the selected product for pricing details
      const selectedProduct = products.find(p => p.id === formData.productId);
      if (!selectedProduct) {
        alert('Please select a warranty product');
        return;
      }

      // Create agreement
      const { data: agreementData, error: agreementError } = await supabase
        .from('warranty_agreements')
        .insert({
          product_id: formData.productId,
          equipment_id: formData.equipmentId,
          homeowner_id: formData.homeownerId,
          contractor_id: formData.contractorId,
          term_months: formData.termMonths,
          start_date: formData.startDate,
          end_date: endDate.toISOString().split('T')[0],
          status: 'pending_delivery',
          deductible_cents: selectedProduct.deductible_cents,
          refrigerant_cap_cents: selectedProduct.refrigerant_cap_cents,
          per_repair_cap_cents: selectedProduct.per_repair_cap_cents,
          aggregate_cap_cents: selectedProduct.aggregate_cap_cents,
          labor_rate_band: selectedProduct.labor_rate_band,
          premium_cents: formData.premiumCents || selectedProduct.base_premium_cents,
          delivery_method: formData.deliveryMethod,
          delivery_deadline: deliveryDeadline.toISOString().split('T')[0]
        })
        .select()
        .single();

      if (agreementError) throw agreementError;

      // Reset form and reload data
      setFormData({
        equipmentId: '',
        productId: '',
        homeownerId: '',
        contractorId: '',
        termMonths: 36,
        startDate: '',
        premiumCents: 0,
        deliveryMethod: 'email'
      });
      setShowCreateForm(false);
      loadData();

    } catch (error) {
      console.error('Error creating agreement:', error);
      alert('Error creating agreement. Please try again.');
    }
  };

  return (
    <AdminPageLayout
      title="📋 Warranty Agreements"
      subtitle="Manage warranty contracts with Florida compliance"
      actions={
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg font-semibold text-white"
        >
          📋 Create Agreement
        </button>
      }
    >
      {/* Stats */}
      <StatsGrid cols={4}>
        <StatCard label="Total Agreements" value={stats.total.toLocaleString()} icon={FileText} />
        <StatCard label="Active" value={stats.active.toLocaleString()} icon={CheckCircle} variant="success" />
        <StatCard label="Pending Delivery" value={stats.pendingDelivery} icon={Clock} variant="warning" />
        <StatCard label="Expiring Soon" value={stats.expiringSoon} icon={AlertTriangle} variant="critical" />
      </StatsGrid>

      {/* Search and Filters */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <input
            type="text"
            placeholder="Search by agreement number..."
            className="p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
          />
          <select className="p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none">
            <option>All Status</option>
            <option>Active</option>
            <option>Pending Delivery</option>
            <option>Cancelled</option>
            <option>Expired</option>
          </select>
          <select className="p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none">
            <option>All Products</option>
            <option>3-Year Labor Only</option>
            <option>5-Year Labor Only</option>
            <option>10-Year Labor Only</option>
            <option>3-Year Parts & Labor</option>
            <option>5-Year Parts & Labor</option>
          </select>
          <input
            type="date"
            placeholder="Start Date From"
            className="p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
          />
          <input
            type="date"
            placeholder="End Date To"
            className="p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
          />
        </div>
      </Card>

      {/* Agreements Table */}
      <Card className="p-6">
        <div className="overflow-x-auto -mx-6">
          <table className="w-full">
            <thead className="bg-slate-800">
              <tr>
                <th className="p-4 text-left text-slate-300 font-semibold">Agreement #</th>
                <th className="p-4 text-left text-slate-300 font-semibold">Equipment</th>
                <th className="p-4 text-left text-slate-300 font-semibold">Homeowner</th>
                <th className="p-4 text-left text-slate-300 font-semibold">Product</th>
                <th className="p-4 text-left text-slate-300 font-semibold">Term</th>
                <th className="p-4 text-left text-slate-300 font-semibold">Start Date</th>
                <th className="p-4 text-left text-slate-300 font-semibold">Status</th>
                <th className="p-4 text-left text-slate-300 font-semibold">Premium</th>
                <th className="p-4 text-left text-slate-300 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="p-8 text-center text-slate-400">
                    <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                    Loading agreements...
                  </td>
                </tr>
              ) : agreements.length === 0 ? (
                <tr>
                  <td colSpan={9} className="p-8 text-center text-slate-400">
                    No warranty agreements found. Click "Create Agreement" to add one.
                  </td>
                </tr>
              ) : (
                agreements.map((agreement) => (
                  <AgreementRow
                    key={agreement.id}
                    agreement={agreement}
                    onViewCertificate={() => setShowCertificate(agreement)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create Agreement Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-900 p-6 rounded-lg border border-slate-800 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">📋 Create New Warranty Agreement</h2>
              <button
                onClick={() => setShowCreateForm(false)}
                className="text-slate-400 hover:text-slate-200"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Equipment *
                  </label>
                  <select
                    value={formData.equipmentId}
                    onChange={(e) => setFormData({...formData, equipmentId: e.target.value})}
                    className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                    required
                  >
                    <option value="">Select equipment</option>
                    {equipment.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.serial_number} - {item.manufacturer?.name} {item.model_number}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Warranty Product *
                  </label>
                  <select
                    value={formData.productId}
                    onChange={(e) => setFormData({...formData, productId: e.target.value})}
                    className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                    required
                  >
                    <option value="">Select warranty product</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name} - ${(product.base_premium_cents / 100).toFixed(2)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Homeowner *
                  </label>
                  <select
                    value={formData.homeownerId}
                    onChange={(e) => setFormData({...formData, homeownerId: e.target.value})}
                    className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                    required
                  >
                    <option value="">Select homeowner</option>
                    {people.map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.first_name} {person.last_name} - {person.email}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Contractor *
                  </label>
                  <select
                    value={formData.contractorId}
                    onChange={(e) => setFormData({...formData, contractorId: e.target.value})}
                    className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                    required
                  >
                    <option value="">Select contractor</option>
                    {contractors.map((contractor) => (
                      <option key={contractor.id} value={contractor.id}>
                        {contractor.legal_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                    className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Term (Months)
                  </label>
                  <input
                    type="number"
                    value={formData.termMonths}
                    onChange={(e) => setFormData({...formData, termMonths: parseInt(e.target.value)})}
                    className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                    min="1"
                    max="120"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Delivery Method
                  </label>
                  <select
                    value={formData.deliveryMethod}
                    onChange={(e) => setFormData({...formData, deliveryMethod: e.target.value})}
                    className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                  >
                    <option value="email">Email</option>
                    <option value="paper">Paper Mail</option>
                    <option value="both">Both Email & Paper</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg"
                >
                  Create Agreement
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Certificate Modal */}
      {showCertificate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-900 p-6 rounded-lg border border-slate-800 max-w-6xl w-full mx-4 max-h-[95vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">📜 Warranty Certificate</h2>
              <button
                onClick={() => setShowCertificate(null)}
                className="text-slate-400 hover:text-slate-200"
              >
                ✕
              </button>
            </div>

            <WarrantyCertificate
              agreement={showCertificate}
              equipment={showCertificate.equipment}
              homeowner={showCertificate.homeowner}
              contractor={showCertificate.contractor}
              property={showCertificate.equipment?.property}
              product={showCertificate.product}
            />
          </div>
        </div>
      )}
    </AdminPageLayout>
  );
};

const AgreementRow = ({
  agreement,
  onViewCertificate
}: {
  agreement: any;
  onViewCertificate: () => void;
}) => {
  const statusColors = {
    active: 'bg-green-900 text-green-400',
    pending_delivery: 'bg-yellow-900 text-yellow-400',
    cancelled: 'bg-red-900 text-red-400',
    expired: 'bg-slate-700 text-slate-400'
  };

  const statusLabels = {
    active: 'Active',
    pending_delivery: 'Pending Delivery',
    cancelled: 'Cancelled',
    expired: 'Expired'
  };

  const formatCurrency = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <tr className="border-b border-slate-800 hover:bg-slate-800/50">
      <td className="p-4">
        <span className="font-mono text-blue-400 font-semibold">
          {agreement.agreement_number || `WA-${agreement.id.slice(0, 8)}`}
        </span>
      </td>
      <td className="p-4 text-slate-300">
        <div>
          <div className="font-semibold">{agreement.equipment?.serial_number}</div>
          <div className="text-sm text-slate-400">
            {agreement.equipment?.manufacturer?.name} {agreement.equipment?.model_number}
          </div>
        </div>
      </td>
      <td className="p-4 text-slate-300">
        <div>
          <div className="font-semibold">
            {agreement.homeowner?.first_name} {agreement.homeowner?.last_name}
          </div>
          <div className="text-sm text-slate-400">{agreement.homeowner?.email}</div>
        </div>
      </td>
      <td className="p-4 text-slate-300">{agreement.product?.name}</td>
      <td className="p-4 text-slate-300">{agreement.term_months} months</td>
      <td className="p-4 text-slate-300">{formatDate(agreement.start_date)}</td>
      <td className="p-4">
        <span className={`px-2 py-1 rounded text-sm font-medium ${statusColors[agreement.status as keyof typeof statusColors]}`}>
          {statusLabels[agreement.status as keyof typeof statusLabels]}
        </span>
      </td>
      <td className="p-4 text-slate-300 font-semibold">
        {formatCurrency(agreement.premium_cents)}
      </td>
      <td className="p-4">
        <div className="flex space-x-2">
          <button
            onClick={onViewCertificate}
            className="text-blue-400 hover:text-blue-300 text-sm"
          >
            📜 Certificate
          </button>
          <button className="text-green-400 hover:text-green-300 text-sm">📧 Deliver</button>
          <button className="text-yellow-400 hover:text-yellow-300 text-sm">✏️ Edit</button>
        </div>
      </td>
    </tr>
  );
};

export default WarrantyAgreementsPage;
